import './styles/SupportCompletionConfetti.css';

const CONFETTI_ROOT_ATTRIBUTE = 'data-support-confetti-root';
const DEFAULT_MIN_VISIBLE_MS = 6000;
const CONFETTI_COLORS = [
  '#ff5a5f',
  '#ff7a18',
  '#ffd166',
  '#06d6a0',
  '#118ab2',
  '#7b61ff',
  '#ff4d8d',
];
const BURST_TYPES = ['ring', 'bloom', 'sparkle'];

let waveInterval = null;
let minDurationTimer = null;
let removeStopListeners = null;
let activeRoot = null;
let canStopOnInteraction = true;
let stopQueuedUntilUnlock = false;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

function randomInt(min, max) {
  return Math.floor(randomBetween(min, max + 1));
}

function randomColor() {
  return CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getActiveConfettiRoot() {
  if (typeof document === 'undefined') {
    return null;
  }
  if (activeRoot?.isConnected) {
    return activeRoot;
  }
  return document.querySelector(`[${CONFETTI_ROOT_ATTRIBUTE}="true"]`);
}

function clearActiveConfettiRoot() {
  const root = getActiveConfettiRoot();
  if (root && root.parentNode) {
    root.parentNode.removeChild(root);
  }
  activeRoot = null;
}

function clearActiveConfettiSession() {
  if (waveInterval) {
    window.clearInterval(waveInterval);
    waveInterval = null;
  }
  if (minDurationTimer) {
    window.clearTimeout(minDurationTimer);
    minDurationTimer = null;
  }
  if (typeof removeStopListeners === 'function') {
    removeStopListeners();
    removeStopListeners = null;
  }
  canStopOnInteraction = true;
  stopQueuedUntilUnlock = false;
  clearActiveConfettiRoot();
}

function attachStopOnUserAction(stopFn) {
  const onPointerDown = () => {
    stopFn();
  };

  const onTouchStart = () => {
    stopFn();
  };

  const onWheel = () => {
    stopFn();
  };

  const onKeyDown = (event) => {
    // Ignore modifier-only shortcuts; any regular key interaction ends the effect.
    if (event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }
    stopFn();
  };

  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('touchstart', onTouchStart, true);
  document.addEventListener('wheel', onWheel, true);
  document.addEventListener('keydown', onKeyDown, true);

  return () => {
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('touchstart', onTouchStart, true);
    document.removeEventListener('wheel', onWheel, true);
    document.removeEventListener('keydown', onKeyDown, true);
  };
}

function requestStopConfetti(stopFn) {
  if (canStopOnInteraction) {
    stopFn();
    return;
  }
  stopQueuedUntilUnlock = true;
}

function buildParticleClass(type) {
  if (type === 'sparkle') {
    return Math.random() > 0.22
      ? 'support-confetti__particle support-confetti__particle--spark'
      : 'support-confetti__particle support-confetti__particle--diamond';
  }

  if (type === 'ring') {
    return Math.random() > 0.5
      ? 'support-confetti__particle support-confetti__particle--ribbon'
      : 'support-confetti__particle support-confetti__particle--diamond';
  }

  return Math.random() > 0.65
    ? 'support-confetti__particle support-confetti__particle--spark'
    : 'support-confetti__particle support-confetti__particle--ribbon';
}

function createBurstFlash({ x, y, reducedMotion, delayMs }) {
  const flash = document.createElement('span');
  const size = reducedMotion ? randomBetween(36, 58) : randomBetween(64, 118);

  flash.className = 'support-confetti__flash';
  flash.style.setProperty('--support-firework-x', `${x}%`);
  flash.style.setProperty('--support-firework-y', `${y}%`);
  flash.style.setProperty('--support-firework-flash-size', `${size}px`);
  flash.style.setProperty('--support-firework-flash-delay', `${delayMs}ms`);
  flash.style.setProperty(
    '--support-firework-flash-duration',
    `${reducedMotion ? randomBetween(380, 560) : randomBetween(620, 900)}ms`
  );

  flash.addEventListener('animationend', () => {
    if (flash.parentNode) {
      flash.parentNode.removeChild(flash);
    }
  });

  return flash;
}

function createFireworkParticle({
  x,
  y,
  angleDeg,
  distance,
  gravity,
  reducedMotion,
  delayMs,
  color,
  type,
}) {
  const particle = document.createElement('span');
  const radians = (angleDeg * Math.PI) / 180;
  const dx = Math.cos(radians) * distance;
  const dy = Math.sin(radians) * distance + gravity;

  const isSpark = type === 'sparkle';
  const size = isSpark
    ? (reducedMotion ? randomBetween(6, 10) : randomBetween(7, 12))
    : (reducedMotion ? randomBetween(8, 12) : randomBetween(10, 18));
  const duration = reducedMotion
    ? randomBetween(920, 1320)
    : randomBetween(1780, 2820);
  const opacity = isSpark
    ? randomBetween(0.75, 1)
    : randomBetween(0.68, 0.95);

  particle.className = buildParticleClass(type);
  particle.style.setProperty('--support-firework-x', `${x}%`);
  particle.style.setProperty('--support-firework-y', `${y}%`);
  particle.style.setProperty('--support-firework-size', `${size.toFixed(2)}px`);
  particle.style.setProperty('--support-firework-delay', `${delayMs.toFixed(0)}ms`);
  particle.style.setProperty('--support-firework-duration', `${duration.toFixed(0)}ms`);
  particle.style.setProperty('--support-firework-opacity', opacity.toFixed(2));
  particle.style.setProperty('--support-firework-color', color);
  particle.style.setProperty('--support-firework-dx', `${dx.toFixed(2)}px`);
  particle.style.setProperty('--support-firework-dy', `${dy.toFixed(2)}px`);
  particle.style.setProperty('--support-firework-rotation-start', `${randomBetween(-120, 120).toFixed(2)}deg`);
  particle.style.setProperty('--support-firework-rotation-end', `${randomBetween(170, 520).toFixed(2)}deg`);

  particle.addEventListener('animationend', () => {
    if (particle.parentNode) {
      particle.parentNode.removeChild(particle);
    }
  });

  return particle;
}

function getBurstConfig(type, reducedMotion) {
  if (type === 'ring') {
    return {
      countMin: reducedMotion ? 9 : 20,
      countMax: reducedMotion ? 16 : 32,
      distanceMin: reducedMotion ? 52 : 110,
      distanceMax: reducedMotion ? 138 : 290,
      angleMode: 'even',
      gravityMin: reducedMotion ? 14 : 30,
      gravityMax: reducedMotion ? 42 : 92,
    };
  }

  if (type === 'sparkle') {
    return {
      countMin: reducedMotion ? 7 : 14,
      countMax: reducedMotion ? 14 : 24,
      distanceMin: reducedMotion ? 34 : 70,
      distanceMax: reducedMotion ? 118 : 210,
      angleMode: 'random',
      gravityMin: reducedMotion ? 12 : 26,
      gravityMax: reducedMotion ? 34 : 74,
    };
  }

  return {
    countMin: reducedMotion ? 10 : 20,
    countMax: reducedMotion ? 19 : 36,
    distanceMin: reducedMotion ? 48 : 92,
    distanceMax: reducedMotion ? 152 : 260,
    angleMode: 'random',
    gravityMin: reducedMotion ? 13 : 28,
    gravityMax: reducedMotion ? 38 : 84,
  };
}

function buildBurstPalette() {
  return [randomColor(), randomColor(), randomColor(), randomColor()];
}

function spawnBurst(root, {
  x,
  y,
  type = 'bloom',
  scale = 1,
  reducedMotion = false,
  delayMs = 0,
}) {
  if (!root?.isConnected) {
    return;
  }

  const config = getBurstConfig(type, reducedMotion);
  const burstPalette = buildBurstPalette();
  const baseCount = randomInt(config.countMin, config.countMax);
  const count = clamp(Math.round(baseCount * scale), 8, 90);

  root.appendChild(createBurstFlash({ x, y, reducedMotion, delayMs }));

  for (let index = 0; index < count; index += 1) {
    const angle = config.angleMode === 'even'
      ? ((360 / count) * index) + randomBetween(-5, 5)
      : randomBetween(0, 360);
    const distance = randomBetween(config.distanceMin, config.distanceMax) * scale;
    const gravity = randomBetween(config.gravityMin, config.gravityMax);
    const color = burstPalette[index % burstPalette.length];
    const particleDelay = delayMs + randomBetween(0, reducedMotion ? 60 : 120);

    root.appendChild(createFireworkParticle({
      x,
      y,
      angleDeg: angle,
      distance,
      gravity,
      reducedMotion,
      delayMs: particleDelay,
      color,
      type,
    }));
  }
}

function spawnOpeningSequence(root, reducedMotion) {
  spawnBurst(root, { x: 50, y: 58, type: 'ring', scale: 1.28, reducedMotion, delayMs: 0 });
  spawnBurst(root, { x: 22, y: 56, type: 'bloom', scale: 1.05, reducedMotion, delayMs: 110 });
  spawnBurst(root, { x: 78, y: 56, type: 'bloom', scale: 1.05, reducedMotion, delayMs: 190 });
  spawnBurst(root, { x: 50, y: 31, type: 'sparkle', scale: 0.98, reducedMotion, delayMs: 300 });
}

function spawnWave(root, reducedMotion) {
  if (!root?.isConnected) {
    return;
  }

  const pattern = randomInt(0, 2);

  if (pattern === 0) {
    spawnBurst(root, { x: 50, y: 56, type: 'ring', scale: 1.12, reducedMotion, delayMs: 0 });
    spawnBurst(root, { x: randomBetween(16, 26), y: randomBetween(43, 57), type: 'sparkle', scale: 0.92, reducedMotion, delayMs: 90 });
    spawnBurst(root, { x: randomBetween(74, 84), y: randomBetween(43, 57), type: 'sparkle', scale: 0.92, reducedMotion, delayMs: 150 });
    return;
  }

  if (pattern === 1) {
    const topY = randomBetween(20, 32);
    spawnBurst(root, { x: 28, y: topY, type: 'sparkle', scale: 0.88, reducedMotion, delayMs: 0 });
    spawnBurst(root, { x: 50, y: topY - 2, type: 'bloom', scale: 1.08, reducedMotion, delayMs: 110 });
    spawnBurst(root, { x: 72, y: topY, type: 'sparkle', scale: 0.88, reducedMotion, delayMs: 220 });
    return;
  }

  spawnBurst(root, {
    x: randomBetween(12, 88),
    y: randomBetween(28, 70),
    type: BURST_TYPES[randomInt(0, BURST_TYPES.length - 1)],
    scale: randomBetween(0.95, 1.28),
    reducedMotion,
    delayMs: 0,
  });
  spawnBurst(root, {
    x: randomBetween(10, 90),
    y: randomBetween(22, 68),
    type: BURST_TYPES[randomInt(0, BURST_TYPES.length - 1)],
    scale: randomBetween(0.84, 1.15),
    reducedMotion,
    delayMs: 150,
  });
}

export function launchSupportCompletionConfetti(options = {}) {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  clearActiveConfettiSession();

  const reducedMotion = prefersReducedMotion();
  const minVisibleMs = clamp(
    Number(options.minVisibleMs) || DEFAULT_MIN_VISIBLE_MS,
    0,
    30000
  );
  const waveIntervalMs = clamp(
    Number(options.waveIntervalMs) || (reducedMotion ? 1850 : 1420),
    700,
    4500
  );

  const confettiRoot = document.createElement('div');
  confettiRoot.className = 'support-confetti';
  confettiRoot.setAttribute(CONFETTI_ROOT_ATTRIBUTE, 'true');
  confettiRoot.setAttribute('aria-hidden', 'true');
  activeRoot = confettiRoot;

  document.body.appendChild(confettiRoot);
  spawnOpeningSequence(confettiRoot, reducedMotion);
  waveInterval = window.setInterval(() => {
    spawnWave(confettiRoot, reducedMotion);
  }, waveIntervalMs);

  const stopOnInteraction = options.stopOnInteraction !== false
    && options.stopOnButtonPress !== false;
  if (stopOnInteraction) {
    canStopOnInteraction = minVisibleMs <= 0;
    stopQueuedUntilUnlock = false;
    removeStopListeners = attachStopOnUserAction(() => requestStopConfetti(clearActiveConfettiSession));

    if (!canStopOnInteraction) {
      minDurationTimer = window.setTimeout(() => {
        canStopOnInteraction = true;
        minDurationTimer = null;
        if (stopQueuedUntilUnlock) {
          clearActiveConfettiSession();
        }
      }, minVisibleMs);
    }
  }
}

export function stopSupportCompletionConfetti() {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }
  clearActiveConfettiSession();
}
